import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useHotelAdminStore } from '../store'
import { apiClient } from '../services/apiClient'
import { extractErrorMessage } from '../utils/errorHandler'

export default function AdminRegisterPage() {
  const navigate = useNavigate()
  const setAdmin = useHotelAdminStore((state) => state.setAdmin)

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    hotel_name: '',
    phone: '',
  })

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }))
  }

  const validateForm = () => {
    if (!formData.email || !formData.password || !formData.full_name || !formData.hotel_name) {
      setError('Пожалуйста, заполните все обязательные поля')
      return false
    }
    
    if (formData.password.length < 8) {
      setError('Пароль должен быть не менее 8 символов')
      return false
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Пожалуйста, введите корректный email')
      return false
    }

    return true
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    
    if (!validateForm()) return

    try {
      setError('')
      setSuccess('')
      setIsLoading(true)
      
      const response = await apiClient.adminRegister(
        formData.email,
        formData.password,
        formData.full_name,
        formData.hotel_name,
        formData.phone || undefined
      )
      
      if (response.access_token) {
        setAdmin({
          ...response,
          access_token: response.access_token,
        })
        setSuccess('✅ Регистрация успешна! Перенаправление на панель управления...')
        
        setTimeout(() => {
          navigate('/admin-dashboard')
        }, 1500)
      } else {
        setError('Ошибка: не получен токен от сервера')
      }
    } catch (err: any) {
      setError(extractErrorMessage(err) || 'Ошибка при регистрации. Попробуйте снова.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleNavigateToLogin = () => {
    navigate('/admin-login')
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50 p-4">
      <div className="w-full max-w-md">
        <div className="bg-white shadow-lg rounded-lg p-8">
          <h1 className="text-3xl font-bold text-indigo-600 mb-2 text-center">HotelCheck</h1>
          <h2 className="text-xl font-semibold text-gray-700 mb-6 text-center">Создание аккаунта администратора</h2>

          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          {success && (
            <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
              {success}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-gray-700 font-semibold mb-2">Email *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="admin@hotel.com"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                disabled={isLoading}
                required
              />
            </div>

            <div>
              <label className="block text-gray-700 font-semibold mb-2">Пароль * (минимум 8 символов)</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="••••••••"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                disabled={isLoading}
                required
              />
            </div>

            <div>
              <label className="block text-gray-700 font-semibold mb-2">Полное имя *</label>
              <input
                type="text"
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                placeholder="Иван Петров"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                disabled={isLoading}
                required
              />
            </div>

            <div>
              <label className="block text-gray-700 font-semibold mb-2">Название отеля *</label>
              <input
                type="text"
                name="hotel_name"
                value={formData.hotel_name}
                onChange={handleChange}
                placeholder="Мой отель"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                disabled={isLoading}
                required
              />
            </div>

            <div>
              <label className="block text-gray-700 font-semibold mb-2">Телефон (опционально)</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+79999999999"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                disabled={isLoading}
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full px-4 py-3 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 text-white rounded-lg font-semibold transition"
            >
              {isLoading ? 'Загрузка...' : 'Создать аккаунт'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-600">
              Уже есть аккаунт?{' '}
              <button
                onClick={handleNavigateToLogin}
                className="text-indigo-600 hover:text-indigo-700 font-semibold"
              >
                Войти
              </button>
            </p>
          </div>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <button
              onClick={() => navigate('/')}
              className="w-full px-4 py-2 text-gray-600 hover:text-gray-800 font-semibold"
            >
              ← Вернуться на главную
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
